import React, { lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { Spin } from 'antd';
import 'antd/dist/antd.min.css';
import { PrivateRoute } from 'components/PrivateRoute';
import { accountAdminMenuKeyState, siteState, uiState } from 'recoil/atoms';
import { deriveSiteFromPath } from 'shared/site';
import { pageMetadata } from 'shared/pageMetadata';
import { ACLCheck } from 'shared/Helpers';
import { isSimplifiedBasicNav } from 'shared/vantageBasic';
import { canRequestBenchmarkReport } from 'shared/requestBenchmarkAccess';
import { ENABLE_WEBINARS } from 'shared/constants';
import { useAuth } from 'hooks';
import './AppVantage.scss';
import { LoadingOutlined } from '@ant-design/icons';
import { CacheBuster } from '@piplup/cache-buster';
import { DealCreationSuccess } from './pages/deal/client/deal-creation/DealCreationSuccess';
import SiteAdminDashboard from 'pages/site-administration/SiteAdminCards';
import Renewals from 'pages/renewals/Renewals';
import KnowledgeGraphTest from './pages/KnowledgeGraphTest';
import { setupGlobalInteractionTracking } from 'shared/observability/prometheus';
// import SmartbenchDashboard from 'pages/dashboard/SmartbenchDashboard';
// import ClientDeals from './pages/deal/client';
// import { Storage } from 'shared/storage';
// import { PortofinoFlyer } from './components/atoms/PortofinoFlyer';
// import { NODE_ENV } from './shared/constants';
// import SiteAdministration from 'pages/site-administration';

// Layouts
const MainLayout = lazy(() => import('./layout/MainLayout'));

// Client-Facing Pages
const ClientDashboard = lazy(() => import('./pages/dashboard/ClientDashboard'));
const SupplierDynamics = lazy(() => import('./pages/supplier-dynamics/SupplierDynamicsHome'));
// const ProductIQ = lazy(() => import('./pages/product-iq/ProductIQHome'));
const UserAdmin = lazy(() => import('./pages/user-admin'));
const VerifyEmail = lazy(() => import('./pages/verify-email'));
// const Renewals = lazy(() => import('./pages/renewals/Renewals'));
const Research = lazy(() => import('./pages/Research/Research'));
const PinPoint = lazy(() => import('./pages/PinPoint'));
const HistoricData = lazy(() => import('./pages/historic-data'));
const SubmittedDealsPage = lazy(() => import('./pages/deal/submitted-deals'));
const DocumentManagement = lazy(() => import('./pages/documents'));
const WebinarsPage = lazy(() => import('./pages/webinars'));
const DealDetails = lazy(() => import('./pages/deal/client'));
const AnalystDeals = lazy(() => import('./pages/deal/analyst-deal/analyst-deal-view/AnalystDeals'));
const AnalystDealsEdit = lazy(() => import('./pages/deal/analyst-deal/analyst-deal-edit/AnalystDealEdit'));

const CalendarDealsPage = lazy(() => import('./pages/deal/client/calendar-deals'));
const SubmitPurchase = lazy(() => import('./pages/deal/purchase-analysis'));
const BenchmarkReportV2 = lazy(() => import('./pages/deal/benchmark-report-v2'));
// "Request a Benchmark Report" (new): reachable by URL only until it is closer to complete — no nav
// entry, and the two existing request screens above stay as they are.
const RequestBenchmarkReport = lazy(() => import('./pages/deal/request'));
const PortfolioCalendar = lazy(() => import('./pages/portfolio/PortfolioCalendar'));
const UploadBulkDealsPage = lazy(() => import('./pages/deal/client/multiple-deals/UploadBulkDealsPage'));
const UploadSingleDeal = lazy(() => import('./pages/deal/single-contract'));
const UploadMultipleDeals = lazy(() => import('./pages/deal/client/multiple-deals/MultipleDealPage'));
// SmartBench Pages
const SmartbenchDashboard = lazy(() => import('./pages/dashboard/SmartbenchDashboard'));
const SmartBenchDashboardIndex = lazy(() => import('./pages/dashboard/SmartBenchDashboardIndex'));
const Contract = lazy(() => import('./pages/contract/Contract'));
const DealView = lazy(() => import('./pages/deal/internal/InternalDeals'));
const Contracts = lazy(() => import('./pages/contract/Contracts'));
const ReportsIndex = lazy(() => import('./pages/reports/ReportsIndex'));
const Reports = lazy(() => import('./pages/reports/Reports'));
const ReportBuilder = lazy(() => import('./pages/reports/ReportBuilder'));
const ReportExport = lazy(() => import('./pages/reports/ReportExport'));
const SkuBenchmark = lazy(() => import('./pages/benchmark/ProductPricingBenchmark'));
const SkuBenchmarkBom = lazy(() => import('./pages/benchmark/ProductPricingBenchmarkBom'));
const DealConfiguration = lazy(() => import('./pages/account-settings'));
const Integrations = lazy(() => import('./pages/account-settings/Integrations'));
const DealManagement = lazy(() => import('./pages/deal/deal-management'));
const NewDeal = lazy(() => import('./pages/deal/NewDeal'));
// The deals screen is the desk switcher, not the open-deals list directly: it owns `deskMode`
// and renders either that list or the Deal Desk closeout worklist. It lazy-loads both, so this
// route keeps the same two chunks it had when it named the list here.
const DealsDeskSwitcher = lazy(() => import('./components/DealsDeskSwitcher/DealsDeskSwitcher'));
const ViewDealPage = lazy(() => import('./pages/deal/NewDeal/ViewDeal'));
const EarlyAccessDealNotFound = lazy(() => import('./components/NewDealComponents/EarlyAccessDealNotFound'));
const DocumentViewerPage = lazy(() => import('./pages/document-viewer/DocumentViewerPage'));

// site admin pages
// const Vendors = lazy(() => import('pages/site-administration/Vendors'));
// const Products = lazy(() => import('./pages/site-administration/Products'));
// const PriceLists = lazy(() => import('./pages/site-administration/PriceLists'));
// const SupplierDynamicsAdmin = lazy(() => import('./pages/site-administration/SupplierDynamicsAdmin'));
// const Resellers = lazy(() => import('./pages/site-administration/Resellers'));
// const Clients = lazy(() => import('./pages/site-administration/Clients'));
// const Users = lazy(() => import('./pages/site-administration/Users'));
// const InquiryAdministration = lazy(() => import('./pages/site-administration/InquiryAdministration'));
// const InquiryWorkbench = lazy(() => import('./pages/site-administration/InquiryWorkbench'));
// const SitePermissions = lazy(() => import('./pages/site-administration/Permissions'));
// const Roles = lazy(() => import('./pages/site-administration/Roles'));
// const ContentModeration = lazy(() => import('./pages/site-administration/ContentModeration'));
// const ReviewModeration = lazy(() => import('./pages/site-administration/ReviewModeration'));
// const ProductAccuracy: FC = lazy(() => import('./pages/site-administration/ProductAccuracy'));
// const BannerAdministration = lazy(() => import('./pages/site-administration/BannerAdministration'));
import Vendors from './pages/site-administration/Vendors';
import Products from './pages/site-administration/Products';
import PriceLists from './pages/site-administration/PriceLists';
import SupplierDynamicsAdmin from './pages/site-administration/SupplierDynamicsAdmin';
import Resellers from './pages/site-administration/Resellers';
import Clients from './pages/site-administration/Clients';
import Users from './pages/site-administration/Users';
import InquiryAdministration from './pages/site-administration/InquiryAdministration';
import InquiryWorkbench from './pages/site-administration/InquiryWorkbench';
import SitePermissions from './pages/site-administration/Permissions';
import Roles from './pages/site-administration/Roles';
import ContentModeration from './pages/site-administration/ContentModeration';
import ReviewModeration from './pages/site-administration/ReviewModeration';
import ProductAccuracy from './pages/site-administration/ProductAccuracy';
import BannerAdministration from './pages/site-administration';
import WebinarAdministration from './pages/site-administration/WebinarAdministration';
import AccountAdmin from './pages/account-admin/AccountAdmin';
import FmvMiner from 'pages/fmv-miner/fmv-miner';
import Aiconfig from 'pages/site-administration/Aiconfig';
import SupplierMapping from './pages/site-administration/SupplierMapping';
import GlobalApiLimits from 'pages/site-administration/GlobalApiLimits';
import PartnerAclAdmin from 'pages/site-administration/PartnerAclAdmin/PartnerAclAdmin';

/**
 * antIcon represents an Ant Design loading icon element with customized style and animation.
 * @returns {React.ReactElement} The loading icon element
 */
const antIcon: React.ReactElement = <LoadingOutlined style={{ fontSize: 96 }} spin />;

/**
 * Redirects the pre-standardization Vantage deal view URL (`/deal_details/:deal_id`) to the
 * standardized `/deal/view/:deal_id` route for early-access users. Query string and router state
 * are preserved; `replace` avoids leaving the old URL in history. Invalid ids fall through to the
 * target route's existing not-found handling (ViewDealPage), so no separate guard is needed here.
 * @returns {React.ReactElement} A declarative redirect to the standardized deal view route.
 */
const LegacyDealViewRedirect: React.FC = (): React.ReactElement => {
  const { deal_id } = useParams<{ deal_id: string }>();
  const { search, state } = useLocation();
  return <Navigate to={`/deal/view/${deal_id}${search}`} replace state={state} />;
};

/**
 * Redirects the pre-standardization Vantage deal edit URL (`/deal_details/:dealId/edit`) to the
 * standardized `/deal/:dealId` route for early-access users, preserving query string and router
 * state. `replace` avoids leaving the old URL in history.
 * @returns {React.ReactElement} A declarative redirect to the standardized deal edit route.
 */
const LegacyDealEditRedirect: React.FC = (): React.ReactElement => {
  const { dealId } = useParams<{ dealId: string }>();
  const { search, state } = useLocation();
  return <Navigate to={`/deal/${dealId}${search}`} replace state={state} />;
};

/**
 * The App variable represents the main application component.
 * It is a functional component that renders the routes and handles the application state.
 * @returns {React.ReactElement} The main application component
 */
const App: React.FC = (): React.ReactElement => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const hasEarlyReleaseRead = ACLCheck(['read:early_access']);
  const simplifiedBasic = isSimplifiedBasicNav();

  const setUi = useSetRecoilState(uiState);
  const setAccountAdminMenu = useSetRecoilState(accountAdminMenuKeyState);
  const setSite = useSetRecoilState(siteState);

  // Setup global interaction tracking once for the entire app
  useEffect(() => {
    const cleanup = setupGlobalInteractionTracking();
    return cleanup;
  }, []);

  useEffect((): void => {
    const res = pageMetadata(location.pathname);
    // Reset accountAdminMenuKey to default
    setAccountAdminMenu('account_summary');
    // localStorage.setItem('lastLocation', location.pathname);
    const showIntro = localStorage.getItem('heroBanner');
    setUi({
      title: res.title,
      breadcrumbs: res.breadcrumbs,
      menu: res.menu,
      menuKey: '',
      showIntro: !showIntro || showIntro === 'opened'
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // The URL is the single source of truth for the active site: all SmartBench screens live under
  // `/sb`, everything else is Vantage. Deriving purely from the pathname (rather than latching the
  // previous value) ensures switching sites always renders the matching header and navigation.
  useEffect((): void => {
    setSite(deriveSiteFromPath(location.pathname));
  }, [location, setSite]);

  /**
   * Represents a loading application component.
   * @returns {React.ReactElement} The loading application component
   */
  const loadingApp: React.ReactElement = (
    <div className="min-h-screen bg-[#ECEFF1] flex justify-center items-center">
      <Spin indicator={antIcon} tip="Securing app .." />
    </div>
  );

  /**
   * The siteAdministrationRoutes variable represents the routes for the site administration section of the application.
   * It is a React element that defines the routes using the React Router library.
   * @returns {React.ReactElement} The site administration routes
   */
  const SiteAdministrationRoutes: React.ReactElement = (
    <Route path="site_administration">
      <Route index element={<PrivateRoute as={SiteAdminDashboard} />} />
      <Route path="suppliers" element={<PrivateRoute as={Vendors} />} />
      <Route path="products" element={<PrivateRoute as={Products} />} />
      <Route path="price_lists" element={<PrivateRoute as={PriceLists} />} />
      <Route path="product_accuracy" element={<PrivateRoute as={ProductAccuracy} />} />
      <Route path="supplier_dynamics" element={<PrivateRoute as={SupplierDynamicsAdmin} />} />
      <Route path="resellers" element={<PrivateRoute as={Resellers} />} />
      <Route path="clients" element={<PrivateRoute as={Clients} />} />
      <Route path="users" element={<PrivateRoute as={Users} />} />
      <Route path="account_admin" element={<PrivateRoute as={AccountAdmin} />} />
      <Route path="supplier_mapping" element={<PrivateRoute as={SupplierMapping} />} />
      <Route path="global_api_limits" element={<PrivateRoute as={GlobalApiLimits} />} />
      <Route path="partner_acl_admin" element={<PrivateRoute as={PartnerAclAdmin} />} />
      <Route path="triage" element={<PrivateRoute as={InquiryAdministration} />} />
      <Route path="workbench" element={<PrivateRoute as={InquiryWorkbench} />} />
      <Route path="permissions" element={<PrivateRoute as={SitePermissions} />} />
      <Route path="roles" element={<PrivateRoute as={Roles} />} />
      <Route path="community_moderation" element={<PrivateRoute as={ContentModeration} />} />
      <Route path="review_moderation" element={<PrivateRoute as={ReviewModeration} />} />
      <Route path="banner_administration" element={<PrivateRoute as={BannerAdministration} />} />
      {ENABLE_WEBINARS && <Route path="webinar_administration" element={<PrivateRoute as={WebinarAdministration} />} />}
      <Route path="ai_config" element={<PrivateRoute as={Aiconfig} />} />
    </Route>
  );

  if (!isAuthenticated && ACLCheck(['read:client_app'])) {
    return loadingApp;
  }

  /**
   * Enables cache busting if running in production or test environment.
   * @type {boolean}
   */
  // const enableCacheBuster: boolean = NODE_ENV === 'production' || NODE_ENV === 'test' || NODE_ENV === 'development';

  return (
    <CacheBuster loading={loadingApp} verbose={true} enabled={true}>
      <>
        {/*{!ACLCheck(['read:portofino']) && !ACLCheck(['read:smartbench_access']) && <PortofinoFlyer />}*/}

        <Routes>
          {/* Document Viewer Route - Standalone without MainLayout */}
          <Route
            path="document-viewer/:documentId/:documentType/:fileName?"
            element={<PrivateRoute as={DocumentViewerPage} />}
          />

          <Route path="/" element={<MainLayout />}>
            {/* Vantage Basic cohort (see shared/vantageBasic.ts): Home renders All Submitted
                Deals in place — the URL stays on /. Everyone else keeps the dashboard, which
                is shared with Pro and must not be removed. */}
            <Route path={'/'} element={<PrivateRoute as={simplifiedBasic ? SubmittedDealsPage : ClientDashboard} />} />
            <Route path="/user_admin" element={<PrivateRoute as={UserAdmin} />} />
            <Route path="/notification_preferences" element={<PrivateRoute as={UserAdmin} />} />
            <Route path="/verify_email" element={<VerifyEmail />} />
            {ACLCheck(['read:pinpoint']) && <Route path="/pinpoint" element={<PrivateRoute as={PinPoint} />} />}
            {ACLCheck(['read:deal_calendar_all', 'read:deal_calendar_company']) && (
              <Route path="/deal_calendar" element={<PrivateRoute as={Renewals} />} />
            )}
            <Route path="/portfolio_calendar" element={<PrivateRoute as={PortfolioCalendar} />} />
            {ACLCheck(['read:own_inquiry']) && (
              <Route path="/research/:community_type" element={<PrivateRoute as={Research} />} />
            )}
            {ACLCheck(['read:supplier_dynamics']) && (
              <Route path="/supplier_dynamics" element={<PrivateRoute as={SupplierDynamics} />} />
            )}
            {/* {ACLCheck(['read:product_iq']) && <Route path="/product_iq" element={<PrivateRoute as={ProductIQ} />} />} */}
            <Route
              path="/subscription_purchases"
              element={<PrivateRoute as={hasEarlyReleaseRead ? SubmittedDealsPage : HistoricData} />}
            />
            {hasEarlyReleaseRead && <Route path="/deals" element={<PrivateRoute as={SubmittedDealsPage} />} />}
            <Route path="/submit_purchase" element={<PrivateRoute as={SubmitPurchase} />} />
            <Route path="/submit_purchase/:deal_id" element={<PrivateRoute as={SubmitPurchase} />} />
            {/*
              New benchmark-request page. The route always registers when early-access applies (so it
              outranks `/deal/:dealId?` below and a caller can never fall through into the edit-mode
              screen), but renders a redirect to `/submit_purchase` unless the caller also holds
              `create:own_company_deals` — the same pair the page itself checks via
              `canRequestBenchmarkReport()`/`isRequestPageAllowed`. The static segment outranks
              `/deal/:dealId?` and `/deal/:deal_id` in React Router's matching, so both existing deal
              routes keep their behaviour.
            */}
            {hasEarlyReleaseRead && (
              <Route
                path="/deal/request"
                element={
                  canRequestBenchmarkReport() ? (
                    <PrivateRoute as={RequestBenchmarkReport} />
                  ) : (
                    <Navigate to="/submit_purchase" replace />
                  )
                }
              />
            )}
            {hasEarlyReleaseRead ? (
              <>
                {/*
                  V2 Vantage deal routes — standardized to mirror the SmartBench (`/sb/deal/...`)
                  pattern: create and edit share one route (a deal id present means edit), and the
                  read-only view lives on its own `/deal/view/:deal_id` path.
                */}
                <Route path="/deal/:dealId?" element={<PrivateRoute as={BenchmarkReportV2} />} />
                <Route path="/deal/view/:deal_id" element={<PrivateRoute as={ViewDealPage} clientView />} />
                {/*
                  Back-compat: forward the pre-standardization Vantage deal URLs to the new routes so
                  existing links, bookmarks, and shared components keep working. The redirect helpers
                  preserve query string and router state.
                */}
                <Route path="/deal_details/:deal_id" element={<LegacyDealViewRedirect />} />
                <Route path="/deal_details/:dealId/edit" element={<LegacyDealEditRedirect />} />
                <Route path="/benchmark-report/new" element={<Navigate to="/deal" replace />} />
              </>
            ) : (
              <>
                {/* Legacy Vantage deal detail view — unchanged for non-early-access users. */}
                <Route path="/deal_details/:deal_id" element={<PrivateRoute as={DealDetails} />} />
                <Route path="/deal/:deal_id" element={<PrivateRoute as={DealDetails} />} />
              </>
            )}
            <Route path="/upload_deals" element={<PrivateRoute as={UploadBulkDealsPage} />} />
            <Route path="/add_single_deal" element={<PrivateRoute as={UploadSingleDeal} />} />
            <Route path="/add_multiple_deals" element={<PrivateRoute as={UploadMultipleDeals} />} />
            {/*<Route path="/single_contract" element={<PrivateRoute as={SingleContract} />} />*/}
            {/* <Route path="/analyst_deals/:deal_id" element={<PrivateRoute as={AnalystDeals} />} /> */}
            {/* <Route path="/analyst_deals_edit/:deal_id" element={<PrivateRoute as={AnalystDealsEdit} />} /> */}
            <Route path="/calendar_deals/:deal_id" element={<PrivateRoute as={CalendarDealsPage} />} />
            {ACLCheck(['read:npi_docs']) && (
              <Route path="resources/:resource_type?" element={<PrivateRoute as={DocumentManagement} />} />
            )}
            {SiteAdministrationRoutes}
            {ACLCheck(['read:site_wide_admin', 'update:banner_ad']) && (
              <Route path="banner_admin" element={<PrivateRoute as={BannerAdministration} />} />
            )}
            {ENABLE_WEBINARS && <Route path="/webinars" element={<PrivateRoute as={WebinarsPage} />} />}
            <Route path="/deal_creation_success" element={<PrivateRoute as={DealCreationSuccess} />} />
            <Route path="/deal_creation_success/:contract_id" element={<PrivateRoute as={DealCreationSuccess} />} />
            <Route path="/account_settings" element={<PrivateRoute as={DealConfiguration} />} />
            <Route path="/integrations" element={<PrivateRoute as={Integrations} />} />

            {ACLCheck(['read:smartbench_access']) && (
              <Route path="sb" element={<PrivateRoute as={SmartBenchDashboardIndex} />}>
                <Route
                  index
                  element={<PrivateRoute as={hasEarlyReleaseRead ? DealsDeskSwitcher : SmartbenchDashboard} />}
                />
                {!hasEarlyReleaseRead && <Route path="contracts" element={<PrivateRoute as={Contracts} />} />}
                <Route path="contract/:contract_id" element={<PrivateRoute as={Contract} />} />
                {hasEarlyReleaseRead ? (
                  <>
                    <Route path="deal/:deal_id?" element={<PrivateRoute as={NewDeal} />} />
                    <Route path="deal/view/:deal_id" element={<PrivateRoute as={ViewDealPage} />} />
                  </>
                ) : (
                  <>
                    <Route path="deal/:deal_id?" element={<PrivateRoute as={EarlyAccessDealNotFound} />} />
                    <Route path="deal/view/:deal_id" element={<PrivateRoute as={EarlyAccessDealNotFound} />} />
                  </>
                )}
                <Route path="deal/:contract_id" element={<PrivateRoute as={DealView} />} />
                {hasEarlyReleaseRead && <Route path="deals" element={<PrivateRoute as={DealsDeskSwitcher} />} />}
                <Route path="knowledge-graph" element={<PrivateRoute as={KnowledgeGraphTest} />} />
                <Route path="reports" element={<PrivateRoute as={ReportsIndex} />}>
                  <Route index element={<PrivateRoute as={Reports} />} />
                  <Route path=":id" element={<PrivateRoute as={ReportExport} />} />
                  <Route path="builder">
                    <Route path=":report_id" element={<PrivateRoute as={ReportBuilder} />} />
                  </Route>
                </Route>
                <Route path="benchmarks/product" element={<PrivateRoute as={SkuBenchmark} />} />
                {hasEarlyReleaseRead && (
                  <Route path="benchmarks-products" element={<PrivateRoute as={SkuBenchmarkBom} />} />
                )}
                <Route path="juggernaut" element={<PrivateRoute as={FmvMiner} />} />
                <Route path="deal-mgmt/:id" element={<PrivateRoute as={DealManagement} />} />
                {/* Analyst Deal Routes */}
                <Route path="analyst_view/:deal_id" element={<PrivateRoute as={AnalystDeals} />} /> {/* VIEW */}
                <Route path="analyst_update/:deal_id" element={<PrivateRoute as={AnalystDealsEdit} />} /> {/* EDIT */}
              </Route>
            )}
          </Route>
        </Routes>
      </>
    </CacheBuster>
  );
};

export default App;
